import { Input } from 'app/components/ui/input'
import { Label } from 'app/components/ui/label'
import { RadioGroup } from 'app/components/ui/radio-group'
import { ComboBoxView } from 'app/components/ui/combo/combo-box'
import { Trash } from 'iconoir-react'
import { Button } from 'app/components/ui/button'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import type { IvrConfig, IvrNode } from 'app/types/ivr'
import { AudioInput } from './AudioInput'
import { DialRow } from './DialRow'
import { AUDIO_TYPE_OPTIONS, DEFAULT_RING_COUNT, NO_ANSWER_OPTIONS } from './ivr-form'
import type { SubOption } from './ivr-form'
import { digitsOnly } from 'lib/phone'

const NODE_TYPE_LABELS: Record<string, string> = {
  greeting: `Greeting`,
  menu: `Greeting`,
  dial: `Dial`,
  voicemail: `Voicemail`,
  businessHours: `Business Hours`,
  announcement: `Announcement`,
}

type NodeEditorProps = {
  config: IvrConfig
  nodeId: string
  rootId: string
  activePlayerId: string | undefined
  onNodeChange: (nodeId: string, updated: Partial<IvrNode>) => void
  onReplaceNode: (nodeId: string, node: IvrNode) => void
  onRemoveNode: (nodeId: string) => void
  onAddMenuOption: (menuNodeId: string, nodeType: `dial` | `voicemail` | `menu`) => void
  onPlay: (id: string | undefined) => void
  onFileSelect: (slot: string, file: File | null) => void
}

function GreetingEditor({
  node,
  nodeId,
  props,
}: {
  node: IvrNode & { type: 'greeting' }
  nodeId: string
  props: NodeEditorProps
}) {
  // The greeting node itself often has empty audio — the actual greeting callers hear
  // is typically on the next node (menu) which plays inside the <Gather>.
  // Show the menu node's audio when the greeting's own audio is empty.
  const nextNode = node.next ? props.config.nodes[node.next] : undefined
  const nextAudio = nextNode?.type === `menu` ? (nextNode as any).audio : undefined
  const ownAudioEmpty = !(node.audio?.type === `tts` && node.audio.text) && node.audio?.type !== `s3`
  const effectiveAudio = ownAudioEmpty && nextAudio ? nextAudio : node.audio || { type: `tts` as const, text: `` }
  const editingNextNode = ownAudioEmpty && nextAudio && node.next

  const targetNodeId = editingNextNode ? node.next! : nodeId
  const audio = effectiveAudio

  return (
    <div className="space-y-4">
      <div>
        <Label className="font-semibold">Greeting Audio</Label>
        <p className="mt-0.5 text-body-xs text-subtle">
          The first message callers hear. Describe your menu options here.
        </p>
      </div>
      <AudioInput
        audioType={audio.type}
        text={audio.type === `tts` ? audio.text : ``}
        audioKey={audio.type === `s3` ? audio.key : ``}
        id={`node-${targetNodeId}`}
        activePlayerId={props.activePlayerId}
        onTextChange={(val) =>
          props.onNodeChange(targetNodeId, { audio: { type: `tts`, text: val } } as Partial<IvrNode>)
        }
        onKeyChange={(val) => props.onNodeChange(targetNodeId, { audio: { type: `s3`, key: val } } as Partial<IvrNode>)}
        onFileSelect={(file) => props.onFileSelect(targetNodeId, file || null)}
        onPlay={props.onPlay}
      />
      <RadioGroup
        values={AUDIO_TYPE_OPTIONS}
        value={audio.type}
        onChange={(val) => {
          const newAudio =
            val === `tts`
              ? { type: `tts` as const, text: audio.type === `tts` ? audio.text : `` }
              : { type: `s3` as const, key: audio.type === `s3` ? audio.key : `` }
          props.onNodeChange(targetNodeId, { audio: newAudio } as Partial<IvrNode>)
        }}
      />
    </div>
  )
}

function MenuEditor({
  node,
  nodeId,
  config,
  props,
}: {
  node: IvrNode & { type: 'menu' }
  nodeId: string
  config: IvrConfig
  props: NodeEditorProps
}) {
  const audio = node.audio || { type: `tts` as const, text: `` }
  const options = node.options || []
  const isRoot = nodeId === config.root || nodeId === props.rootId

  // Compute menu depth: root=1, sub-menu=2, sub-sub-menu=3
  // Show "+ Add Sub-menu" only at depth < 3
  const canAddSubMenu = (() => {
    const parentOf = new Map<string, string>()
    for (const [id, n] of Object.entries(config.nodes)) {
      if (n.type === `menu`) {
        n.options.forEach((o) => parentOf.set(o.targetNodeId, id))
      }
    }
    let depth = 1
    let current = nodeId
    while (parentOf.has(current)) {
      current = parentOf.get(current)!
      depth++
    }
    return depth < 3
  })()

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <Label className="font-semibold">{isRoot ? `Greeting Message` : `Sub-menu Greeting`}</Label>
          <p className="mt-0.5 text-body-xs text-subtle">
            {isRoot
              ? `The first message callers hear. Describe your menu options here.`
              : `Played before gathering caller input.`}
          </p>
        </div>
        {!isRoot && (
          <button
            onClick={() => props.onRemoveNode(nodeId)}
            className="rounded p-1.5 text-minimal hover:bg-surface-subtle hover:text-destructive"
            title="Remove"
          >
            <Trash className="h-4 w-4" />
          </button>
        )}
      </div>
      {!isRoot && (
        <div>
          <Label className="text-body-xs">Label</Label>
          <Input
            value={((node as Record<string, unknown>).label as string) || ``}
            onChange={(e) => props.onNodeChange(nodeId, { label: e.target.value } as Partial<IvrNode>)}
            placeholder="e.g. New Patients, Billing"
            className="mt-1"
          />
        </div>
      )}
      <AudioInput
        audioType={audio.type}
        text={audio.type === `tts` ? audio.text : ``}
        audioKey={audio.type === `s3` ? audio.key : ``}
        id={`node-${nodeId}`}
        activePlayerId={props.activePlayerId}
        onTextChange={(val) => props.onNodeChange(nodeId, { audio: { type: `tts`, text: val } } as Partial<IvrNode>)}
        onKeyChange={(val) => props.onNodeChange(nodeId, { audio: { type: `s3`, key: val } } as Partial<IvrNode>)}
        onFileSelect={(file) => props.onFileSelect(nodeId, file || null)}
        onPlay={props.onPlay}
      />
      <RadioGroup
        values={AUDIO_TYPE_OPTIONS}
        value={audio.type}
        onChange={(val) => {
          const newAudio =
            val === `tts`
              ? { type: `tts` as const, text: audio.type === `tts` ? audio.text : `` }
              : { type: `s3` as const, key: audio.type === `s3` ? audio.key : `` }
          props.onNodeChange(nodeId, { audio: newAudio } as Partial<IvrNode>)
        }}
      />

      <div className="mt-4">
        <Label className="font-semibold">Options</Label>
        <div className="mt-2 space-y-2">
          {options.map(({ digit, targetNodeId }) => {
            const target = config.nodes[targetNodeId]
            const label = (target as any)?.label || NODE_TYPE_LABELS[target?.type] || `Untitled`
            return (
              <div
                key={digit}
                className="flex items-center gap-2 rounded-lg border border-default bg-surface-subtle px-3 py-2"
              >
                <span className="flex h-5 w-5 items-center justify-center rounded bg-primary-default text-xs font-bold text-on-color">
                  {digit}
                </span>
                <span className="flex-1 text-ui-xs text-default">{label}</span>
                <button
                  onClick={() => props.onRemoveNode(targetNodeId)}
                  className="text-minimal hover:text-destructive"
                >
                  <Trash className="h-3.5 w-3.5" />
                </button>
              </div>
            )
          })}
        </div>
        <div className="mt-3 flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => props.onAddMenuOption(nodeId, `dial`)}
            disabled={options.length >= 9}
          >
            + Add Dial
          </Button>
          {canAddSubMenu && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => props.onAddMenuOption(nodeId, `menu`)}
              disabled={options.length >= 9}
            >
              + Add Sub-menu
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

function DialEditor({
  node,
  nodeId,
  config,
  props,
}: {
  node: IvrNode & { type: 'dial' }
  nodeId: string
  config: IvrConfig
  props: NodeEditorProps
}) {
  const noAnswerTarget = node.noAnswer ? config.nodes[node.noAnswer] : undefined
  const noAnswerType = noAnswerTarget?.type === `dial` ? `forward` : `voicemail`

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <Label className="font-semibold">Dial Configuration</Label>
          <p className="mt-0.5 text-body-xs text-subtle">Ring a phone number and handle no-answer.</p>
        </div>
        {nodeId !== props.rootId && (
          <button
            onClick={() => props.onRemoveNode(nodeId)}
            className="rounded p-1.5 text-minimal hover:bg-surface-subtle hover:text-destructive"
            title="Remove"
          >
            <Trash className="h-4 w-4" />
          </button>
        )}
      </div>
      <div className="space-y-3">
        <div>
          <Label className="text-body-xs">Label</Label>
          <Input
            value={(node as any).label || ``}
            onChange={(e) => props.onNodeChange(nodeId, { label: e.target.value } as Partial<IvrNode>)}
            placeholder="e.g. New Patient, Front Desk"
            className="mt-1"
          />
        </div>
        <div>
          <Label className="text-body-xs">Phone Number</Label>
          <Input
            value={formatPhoneNumber(node.target) || node.target || ``}
            onChange={(e) =>
              props.onNodeChange(nodeId, { target: e.target.value.replace(/[^0-9$]/g, ``) } as Partial<IvrNode>)
            }
            placeholder="(555) 123-4567"
            className="mt-1"
          />
        </div>
        <div className="flex items-center gap-4">
          <div>
            <Label className="text-body-xs">Ring Count</Label>
            <Input
              type="number"
              value={node.ringCount ?? DEFAULT_RING_COUNT}
              onChange={(e) => props.onNodeChange(nodeId, { ringCount: Number(e.target.value) } as Partial<IvrNode>)}
              className="mt-1 w-20 text-center"
              min={1}
              max={20}
            />
          </div>
          <div className="flex-1">
            <Label className="text-body-xs">No Answer</Label>
            <div className="mt-1 w-40">
              <ComboBoxView
                key={`${nodeId}-${noAnswerType}`}
                options={NO_ANSWER_OPTIONS}
                value={{
                  id: noAnswerType,
                  name: noAnswerType === `forward` ? `Forward to...` : `Voicemail`,
                  value: noAnswerType,
                }}
                onChange={(val) => {
                  if (node.noAnswer) {
                    if (val.value === `voicemail`) {
                      props.onReplaceNode(node.noAnswer, { type: `voicemail`, email: `` })
                    } else if (val.value === `forward`) {
                      props.onReplaceNode(node.noAnswer, { type: `dial`, target: ``, ringCount: DEFAULT_RING_COUNT })
                    }
                  }
                }}
                inputClassName="h-8! text-xs"
              />
            </div>
          </div>
        </div>
        {noAnswerType === `forward` && noAnswerTarget?.type === `dial` && (
          <div>
            <Label className="text-body-xs">Forward To</Label>
            <Input
              value={formatPhoneNumber(noAnswerTarget.target) || noAnswerTarget.target || ``}
              onChange={(e) =>
                props.onNodeChange(node.noAnswer!, {
                  target: digitsOnly(e.target.value),
                } as Partial<IvrNode>)
              }
              placeholder="(555) 123-4599"
              className="mt-1"
            />
          </div>
        )}
      </div>
    </div>
  )
}

function VoicemailEditor({
  node,
  nodeId,
  props,
}: {
  node: IvrNode & { type: 'voicemail' }
  nodeId: string
  props: NodeEditorProps
}) {
  const audio = node.audio || { type: `tts` as const, text: `` }
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <Label className="font-semibold">Voicemail</Label>
          <p className="mt-0.5 text-body-xs text-subtle">Plays a greeting then records the caller's message.</p>
        </div>
        {nodeId !== props.rootId && (
          <button
            onClick={() => props.onRemoveNode(nodeId)}
            className="rounded p-1.5 text-minimal hover:bg-surface-subtle hover:text-destructive"
            title="Remove"
          >
            <Trash className="h-4 w-4" />
          </button>
        )}
      </div>
      {node.audio && (
        <>
          <AudioInput
            audioType={audio.type}
            text={audio.type === `tts` ? audio.text : ``}
            audioKey={audio.type === `s3` ? audio.key : ``}
            id={`node-${nodeId}`}
            activePlayerId={props.activePlayerId}
            onTextChange={(val) =>
              props.onNodeChange(nodeId, { audio: { type: `tts`, text: val } } as Partial<IvrNode>)
            }
            onKeyChange={(val) => props.onNodeChange(nodeId, { audio: { type: `s3`, key: val } } as Partial<IvrNode>)}
            onFileSelect={(file) => props.onFileSelect(nodeId, file || null)}
            onPlay={props.onPlay}
          />
          <RadioGroup
            values={AUDIO_TYPE_OPTIONS}
            value={audio.type}
            onChange={(val) => {
              const newAudio = val === `tts` ? { type: `tts` as const, text: `` } : { type: `s3` as const, key: `` }
              props.onNodeChange(nodeId, { audio: newAudio } as Partial<IvrNode>)
            }}
          />
        </>
      )}
      <div>
        <Label className="text-body-xs">Notification Email</Label>
        <Input
          value={node.email || ``}
          onChange={(e) => props.onNodeChange(nodeId, { email: e.target.value } as Partial<IvrNode>)}
          placeholder="office@example.com"
          className="mt-1"
        />
      </div>
    </div>
  )
}

function BusinessHoursEditor({
  node,
  nodeId,
  config,
}: {
  node: IvrNode & { type: 'businessHours' }
  nodeId: string
  config: IvrConfig
}) {
  const duringNode = node.duringHours ? config.nodes[node.duringHours] : undefined
  const afterNode = node.afterHours ? config.nodes[node.afterHours] : undefined

  const duringLabel = duringNode
    ? (duringNode as any).label ||
      (duringNode.type === `menu` && (duringNode as any).audio?.text
        ? `"${(duringNode as any).audio.text.slice(0, 50)}..."`
        : NODE_TYPE_LABELS[duringNode.type])
    : `Not set`
  const afterLabel = afterNode
    ? (afterNode as any).label ||
      (afterNode.type === `announcement` && (afterNode as any).audio?.text
        ? `"${(afterNode as any).audio.text.slice(0, 50)}..."`
        : NODE_TYPE_LABELS[afterNode.type])
    : `Not set`

  return (
    <div className="space-y-4">
      <p className="text-body-xs text-subtle">
        Routes callers based on Location open hours. Configure hours in Location Settings → General.
      </p>
      <div className="space-y-2">
        <div className="rounded-lg border border-default bg-surface-subtle px-3 py-2">
          <span className="text-ui-xs font-medium text-default">During Hours</span>
          <p className="mt-0.5 text-body-xs text-subtle">{duringLabel}</p>
        </div>
        <div className="rounded-lg border border-default bg-surface-subtle px-3 py-2">
          <span className="text-ui-xs font-medium text-default">After Hours</span>
          <p className="mt-0.5 text-body-xs text-subtle">{afterLabel}</p>
        </div>
      </div>
    </div>
  )
}

function AnnouncementEditor({
  node,
  nodeId,
  props,
}: {
  node: IvrNode & { type: 'announcement' }
  nodeId: string
  props: NodeEditorProps
}) {
  const audio = node.audio || { type: `tts` as const, text: `` }
  return (
    <div className="space-y-4">
      <div>
        <Label className="font-semibold">Announcement</Label>
        <p className="mt-0.5 text-body-xs text-subtle">Plays a message, then continues or hangs up.</p>
      </div>
      <AudioInput
        audioType={audio.type}
        text={audio.type === `tts` ? audio.text : ``}
        audioKey={audio.type === `s3` ? audio.key : ``}
        id={`node-${nodeId}`}
        activePlayerId={props.activePlayerId}
        onTextChange={(val) => props.onNodeChange(nodeId, { audio: { type: `tts`, text: val } } as Partial<IvrNode>)}
        onKeyChange={(val) => props.onNodeChange(nodeId, { audio: { type: `s3`, key: val } } as Partial<IvrNode>)}
        onFileSelect={(file) => props.onFileSelect(nodeId, file || null)}
        onPlay={props.onPlay}
      />
      <RadioGroup
        values={AUDIO_TYPE_OPTIONS}
        value={audio.type}
        onChange={(val) => {
          const newAudio = val === `tts` ? { type: `tts` as const, text: `` } : { type: `s3` as const, key: `` }
          props.onNodeChange(nodeId, { audio: newAudio } as Partial<IvrNode>)
        }}
      />
      {node.next && <p className="text-body-xs text-subtle">Then continues to: {node.next}</p>}
    </div>
  )
}

export function IvrNodeEditor(props: NodeEditorProps) {
  const { config, nodeId } = props
  const node = config.nodes[nodeId]

  if (!node) {
    return (
      <div className="flex flex-1 items-center justify-center p-6">
        <p className="text-body-xs text-minimal">Select a node from the tree to edit it.</p>
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-y-auto p-6">
      {node.type === `greeting` && <GreetingEditor node={node} nodeId={nodeId} props={props} />}
      {node.type === `menu` && <MenuEditor node={node} nodeId={nodeId} config={config} props={props} />}
      {node.type === `dial` && <DialEditor node={node} nodeId={nodeId} config={config} props={props} />}
      {node.type === `voicemail` && <VoicemailEditor node={node} nodeId={nodeId} props={props} />}
      {node.type === `businessHours` && <BusinessHoursEditor node={node} nodeId={nodeId} config={config} />}
      {node.type === `announcement` && <AnnouncementEditor node={node} nodeId={nodeId} props={props} />}
    </div>
  )
}
